import { DotProps } from 'recharts';

type Props = DotProps & {
  color: string;
  /** Overall dot opacity, e.g. to dim it in sync with its line's `strokeOpacity` on hover. Defaults to fully opaque. */
  opacity?: number;
  /**
   * Click callback that receives the dot's OWN props — including the `payload`
   * row Recharts injects for the point it rendered.
   *
   * Prefer this over `onClick` whenever the handler needs the data behind the
   * point. Recharts' `adaptEventHandlers` rewrites every prop whose name is in
   * its DOM-event allowlist (`onClick` included) when it clones an `activeDot`,
   * re-invoking it as `handler(elementProps, event)` where `elementProps` are
   * the props DECLARED on the element — not the per-point props. So an
   * `onClick` on an `activeDot` never sees `payload`, and the active dot is
   * exactly what the user clicks (it renders on top while hovering).
   * `onPointSelect` isn't in that allowlist, so it survives untouched.
   */
  onPointSelect?: (props: DotProps, event: React.MouseEvent<SVGCircleElement>) => void;
};

export const LineChartDot = ({ color, opacity, onPointSelect, ...props }: Props) => {
  const { cx, cy, onClick, payload, dataKey } = props as DotProps & {
    payload: { date: string; [key: string]: string | number | null };
    dataKey: string;
  };

  const isEmpty = (value: string | number | null | undefined) => value === null || value === undefined;

  if (isEmpty(cx) || isEmpty(cy) || isEmpty(payload[dataKey])) {
    return null;
  }

  const isInteractive = Boolean(onClick || onPointSelect);

  const handleClick = (event: React.MouseEvent<SVGCircleElement>) => {
    onClick?.(props, event);
    onPointSelect?.(props, event);
  };

  return (
    <g opacity={opacity}>
      <circle
        cx={cx}
        cy={cy}
        r={15}
        fill="transparent"
        stroke="transparent"
        strokeWidth={0}
        onClick={handleClick}
        style={{ cursor: isInteractive ? 'pointer' : 'default' }}
        pointerEvents="all"
      />
      <circle cx={cx} cy={cy} r={8} fill={color} fillOpacity={0.2} pointerEvents="none" />
      <circle cx={cx} cy={cy} r={3.5} stroke="none" fill={color} pointerEvents="none" />
    </g>
  );
};
